import EngineClient from './EngineClient.mjs';

// Owns one lazy UCI client/worker at a time. Classic never starts NNUE.
// When NNUE is selected, position failures can fall back for that position;
// startup failures fall back until the user changes the engine or reloads.
export default class FallbackEngineClient {
  constructor({
    selectedEngine = 'classic',
    fallbackEnabled = false, fallbackUrl,
    primaryName = 'Stockfish NNUE', primaryShortName = 'Stockfish NNUE',
    fallbackName = 'Stockfish', fallbackShortName = 'Stockfish',
    onStatus = () => {}, onInfo = () => {},
    Client = EngineClient, ...options
  }) {
    Object.assign(this, { fallbackEnabled, fallbackUrl, primaryName,
      primaryShortName, fallbackName, fallbackShortName, onStatus, onInfo,
      Client, options });
    this.client = null;
    this.selectedEngine = selectedEngine === 'nnue' ? 'nnue' : 'classic';
    this.mode = this.selectedEngine === 'classic' ? 'js' : 'nnue';
    this.request = null;
    this.sessionFallback = false;
    this.positionFallback = null;
    this.generation = 0;
    this.disposed = false;
  }

  selectEngine(engine) {
    if (this.disposed || !['classic', 'nnue'].includes(engine) ||
        engine === this.selectedEngine) return;
    this.request = null;
    this.generation++;
    this.client?.dispose();
    this.client = null;
    this.selectedEngine = engine;
    this.mode = engine === 'classic' ? 'js' : 'nnue';
    this.sessionFallback = false;
    this.positionFallback = null;
    this.emit('off', '', { resetEvals: true });
  }

  emit(status, message = '', details = {}) {
    if (this.disposed) return;
    this.onStatus({
      ...details, status, message, engine: this.mode,
      engineName: this.mode === 'js' ? this.fallbackName : this.primaryName,
      engineShortName: this.mode === 'js' ? this.fallbackShortName : this.primaryShortName,
    });
  }

  replace(mode) {
    this.generation++;
    this.client?.dispose();
    this.mode = mode;
    const generation = this.generation;
    this.client = new this.Client({
      ...this.options,
      url: mode === 'js' ? this.fallbackUrl : this.options.url,
      onInfo: info => {
        if (!this.disposed && generation === this.generation && this.request) {
          this.onInfo({ ...info, engine: mode });
        }
      },
      onStatus: event => {
        if (this.disposed || generation !== this.generation || !this.request) return;
        const refused = event.status === 'no-moves';
        const failed = event.status === 'error';
        if ((refused || failed) && mode === 'nnue' &&
            this.fallbackEnabled && this.fallbackUrl) {
          if (failed && event.failureStage === 'load') this.sessionFallback = true;
          else this.positionFallback = this.request.position;
          const request = this.request;
          this.replace('js');
          this.emit('loading', 'Loading Stockfish…', { resetEvals: true, preserveEvals: true });
          this.client.analyze(request);
          return;
        }
        if (failed) {
          this.emit('error', event.message || (event.failureStage === 'load'
            ? 'Engine could not load. Toggle off and on to retry.'
            : 'Engine stopped unexpectedly. Toggle off and on to retry.'), event);
        } else if (refused) {
          this.emit('no-moves', 'Engine cannot analyze this position', event);
        } else {
          this.emit(event.status, event.status === 'loading'
            ? (mode === 'js' ? 'Loading Stockfish…' : 'Loading Stockfish NNUE…')
            : event.message, event);
        }
      },
    });
  }

  analyze({ fen, position = `position fen ${fen}`, infinite = false }) {
    if (this.disposed) return;
    this.request = { fen, position, infinite };
    if (this.positionFallback !== position) this.positionFallback = null;
    const useClassic = this.selectedEngine === 'classic' ||
      (this.fallbackEnabled && this.fallbackUrl &&
        (this.sessionFallback || this.positionFallback === position));
    const mode = useClassic ? 'js' : 'nnue';
    if (!this.client || mode !== this.mode) {
      this.replace(mode);
      this.emit('loading', mode === 'js' ? 'Loading Stockfish…' : 'Loading Stockfish NNUE…',
        { resetEvals: true });
    }
    this.client.analyze(this.request);
  }

  pause() {
    this.request = null;
    this.client?.pause();
    this.emit('off');
  }

  dispose() {
    this.disposed = true;
    this.request = null;
    this.generation++;
    this.client?.dispose();
    this.client = null;
  }
}
